<template>
  <nav
    class="category-navigation"
    :class="{ 'category-navigation--scrolled': scrolled, 'logged-in': $auth && $auth.loggedIn }"
    aria-label="Seitennavigation"
  >
    <div class="category-navigation__list">
      <div
        v-for="item in menuItems"
        :key="item.id"
        class="category-navigation__list__item"
      >
        <a
          :href="`#${item.id}`"
          class="category-navigation__list__item__link"
          :class="{ 'category-navigation__list__item__link--active': selectedId === item.id }"
          @click.prevent="scrollToSection(item.id)"
        >
          {{ item.label }}
        </a>
      </div>
    </div>
  </nav>
</template>

<script setup lang="ts">
type CategoryMenu = {
  id: string;
  label: string;
};

const props = defineProps<{
  menuItems: CategoryMenu[];
  defaultMenuItemId?: string;
  offset?: number;
}>()

const threshold: number = 90
const thresholdUp: number = 90

const selectedId = ref<string | undefined>(props.defaultMenuItemId)
const scrolled = ref<boolean>(false)
let isScrollingToSection = false

const updateActiveSection = () => {
  handleScroll()
  if (isScrollingToSection) {
    return
  }
  const scrollPosition = window.scrollY + (props.offset ?? 0) + 10
  const atBottom = window.scrollY > 0 && (window.innerHeight + window.scrollY) >= (document.documentElement.scrollHeight - 2)

  if (atBottom && props.menuItems.length > 0) {
    selectedId.value = props.menuItems[props.menuItems.length - 1].id
    return
  }

  let activeId = props.menuItems[0]?.id

  for (const item of props.menuItems) {
    const el = document.getElementById(item.id)
    if (!el) {
      continue
    }

    const elTop = el.getBoundingClientRect().top + window.scrollY - (props.offset ?? 0)

    if (scrollPosition >= elTop) {
      activeId = item.id
    }
  }

  if (activeId) {
    selectedId.value = activeId
  }
}

const handleScroll = () => {
  const scrollY = window.scrollY

  if (!scrolled.value && scrollY > threshold) {
    scrolled.value = true
  } else if (scrolled.value && scrollY < thresholdUp) {
    scrolled.value = false
  }
}

const handleScrollEnd = () => {
  if (window.scrollY > 0 && window.scrollY < thresholdUp) {
    window.scrollTo({top: 0, behavior: 'smooth'})
  }
}

const scrollToSection = (id: string) => {
  selectedId.value = id
  const el = document.getElementById(id)
  if (!el) {
    return
  }

  isScrollingToSection = true

  const top = id === 'descriptionSection'
    ? 0
    : el.getBoundingClientRect().top + window.scrollY - (props.offset ?? 0)

  window.scrollTo({
    top,
    behavior: 'smooth'
  })

  const onScrollEnd = () => {
    isScrollingToSection = false
    window.removeEventListener('scrollend', onScrollEnd)
  }
  window.addEventListener('scrollend', onScrollEnd)

  history.replaceState(null, '', `#${id}`)
}

const initScroll = () => {
  const hash = window.location.hash ? window.location.hash.substring(1) : ''
  const id = hash || props.menuItems[0]?.id || props.defaultMenuItemId
  selectedId.value = id

  if (!window.location.hash) {
    history.replaceState(null, '', `#${selectedId.value}`)
  } else if (hash === 'resultFilterSection') {
    setTimeout(() => {
      scrollToSection('resultFilterSection')
    }, 400)
  }
}

onMounted(() => {
  updateActiveSection()
  initScroll()
  window.addEventListener('scroll', updateActiveSection)
  window.addEventListener('scrollend', handleScrollEnd)
})

onUnmounted(() => {
  window.removeEventListener('scroll', updateActiveSection)
  window.removeEventListener('scrollend', handleScrollEnd)
})
</script>

<style lang="scss">
.category-navigation {
  position: sticky;
  top: 6.9rem;
  z-index: 11;
  background-color: $color__fst__blue__020;
  height: 3rem;
  padding: 0 1rem ;

  &--scrolled {
    box-shadow: 0 .188rem .375rem $color--nav__main--background;
  }

  &__list {
    display: flex;
    align-items: center;
    gap: 1rem;
    height: 100%;
    transition: max-height 0.15s ease-out, opacity 0.15s ease-out;

    &__item {
      padding: 0.5625rem 1rem;
      font-size: 1.125rem;
      line-height: 1.4625rem;
      text-align: center;

      a {
        min-height: 1.875rem;
        display: inline-block;
        align-content: center;
      }

      &__link {
        text-decoration: none!important;
        cursor: pointer;
        font-weight: 300;
        color: $color__primary;
        position: relative;

        &::after {
          content: '';
          position: absolute;
          bottom: 0;
          width: 0;
          right: 0;
          display: inline-block;
          height: 0.125rem;
          transition: width 0.2s ease-in;
          background-color: $color__primary;
        }

        &--active {
          font-weight: $base-text-font-weight-bold;

          &::after {
            left: 0;
            width: 100%;
            transition: width 0.2s ease-out;
          }
        }

        @media (hover: hover) and (pointer: fine) {
          &:hover {
            font-weight: $base-text-font-weight-bold;
            text-decoration: none;
            color: $color__text__link;

            &::after {
              left: 0;
              width: 100%;
              transition: width 0.2s ease-out;
              background-color: $color__text__link;
            }
          }
        }
      }

      @include breakpoint-down($md) {
        font-size: .875rem;
        line-height: 1.1375rem;
      }
    }
  }

  @include breakpoint-down($md) {
    top: 3.5rem;
    padding-inline: 0;

    &__list {
      gap: 0;
    }

    &.logged-in {
      top: 5.75rem;
    }
  }
}
</style>
